import { getRewardById } from '../data/rewardCatalog'
import { nearestTitleColor, type TitleColorKey } from './titlePalette'

/*
  Die **Farbe eines Projekt-Icons** für die Sidebar (Auswahlfläche): eine der
  16 Titel-Farben, beim Wählen einmal ermittelt und in `Project.iconColor`
  gespeichert. Lucide-Icons bringen ihre gewählte Farbe mit; Happy Emojis und
  Emojis werden auf die nächste Titel-Farbe gerundet.
*/

export function hslToRgb([h, s, l]: readonly [number, number, number]): [number, number, number] {
  const sat = s / 100
  const light = l / 100
  const k = (n: number) => (n + h / 30) % 12
  const a = sat * Math.min(light, 1 - light)
  const f = (n: number) => light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)]
}

/** Die Squircle-Farbe des Katalogs — sie ist genau dafür da, das Bild zu tragen. */
export function happyIconColor(rewardId: string): TitleColorKey | null {
  const reward = getRewardById(rewardId)
  return reward ? nearestTitleColor(hslToRgb(reward.hsl)) : null
}

/**
 * Die Durchschnittsfarbe eines Emojis in der Systemschrift, gewichtet nach
 * Deckkraft und Buntheit — ein gelbes Gesicht mit schwarzen Augen ist gelb.
 * `null`, wo kein Canvas zeichnet (Tests, sehr alte Umgebungen).
 */
export function measureEmojiRgb(emoji: string): [number, number, number] | null {
  let ctx: CanvasRenderingContext2D | null = null
  try {
    ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
  } catch {
    return null
  }
  if (!ctx) return null
  const size = 32
  ctx.canvas.width = ctx.canvas.height = size
  ctx.font = `${size - 4}px 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(emoji, size / 2, size / 2)
  const data = ctx.getImageData(0, 0, size, size).data
  let r = 0
  let g = 0
  let b = 0
  let weight = 0
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3] / 255
    if (alpha < 0.5) continue
    const max = Math.max(data[i], data[i + 1], data[i + 2])
    const min = Math.min(data[i], data[i + 1], data[i + 2])
    const w = alpha * (0.15 + (max - min) / 255)
    r += data[i] * w
    g += data[i + 1] * w
    b += data[i + 2] * w
    weight += w
  }
  if (!weight) return null
  return [Math.round(r / weight), Math.round(g / weight), Math.round(b / weight)]
}

export function emojiIconColor(emoji: string): TitleColorKey | null {
  const rgb = measureEmojiRgb(emoji)
  return rgb ? nearestTitleColor(rgb) : null
}
